import {
  Button,
  InputDay,
  InputSelect,
  InputWrapper,
  type SelectItem,
} from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { Modal } from '@vrplatform/components-legacy';
import { type Maybe, sortBy, toTitleCase } from '@vrplatform/utils';
import {
  Avatar,
  Box,
  Collapse,
  Group,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  getExtractDateRangeMethod,
  shouldShowExtractDateRangeInput,
} from './ExtractModal.utils';
import { useExtractModal } from './useExtractModal';
import { useExtractModalQuery } from './useExtractModalQuery';
import {
  EXTRACT_ALL_KEY,
  useExtractModalMutation,
} from './useExtractModalMutation';

// allow to extract all of connection
// allow to extract only certain key from connection
// prefill modal when opening from mapping
// some systems allow extracting by date ranges

type FormInputs = {
  dateRange: [Date | null, Date | null] | undefined | null;
  extractType: Maybe<string>;
};

export const ExtractModal = () => {
  const {
    close: closeModal,
    opened,
    data: { extractConnectionIds, extractType },
  } = useExtractModal();

  const { runExtraction } = useExtractModalMutation({
    closeModal,
    extractConnectionIds,
  });

  const { connection, loading, refetch } = useExtractModalQuery({
    extractConnectionIds,
    opened,
  });

  const extractors = connection?.extractors || [];

  const methods = useForm<FormInputs>({
    values: {
      extractType:
        extractType && extractors.some((x) => x.name === extractType)
          ? extractType
          : undefined,
      dateRange: undefined,
    },
  });

  const selectedExtractType = methods.watch('extractType');

  const showDateRangeInput = useMemo(
    () =>
      shouldShowExtractDateRangeInput({
        extractors,
        extractType: selectedExtractType,
        extractAllKey: EXTRACT_ALL_KEY,
      }),
    [extractors, selectedExtractType]
  );

  const dateRangeMethod = useMemo(
    () =>
      getExtractDateRangeMethod({
        extractors,
        extractType: selectedExtractType,
        extractAllKey: EXTRACT_ALL_KEY,
      }),
    [extractors, selectedExtractType]
  );

  useEffect(() => {
    if (opened) {
      methods.reset({ extractType, dateRange: [null, null] });
    }
  }, [extractType, opened, methods.reset]);

  useEffect(() => {
    if (!showDateRangeInput) {
      methods.setValue('dateRange', undefined);
    }
  }, [showDateRangeInput, methods.setValue]);

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      radius="md"
      title={
        extractConnectionIds.length > 1 ? (
          <Group wrap="nowrap" gap="sm">
            <Icon icon="RefreshCwIcon" size={18} />

            <Text fw={500} size="md">
              Fetch connections
            </Text>
          </Group>
        ) : loading ? (
          ''
        ) : connection ? (
          <Group wrap="nowrap">
            <Avatar size="sm" src={connection.logo} />
            <Text lineClamp={1} size="md" fw={500}>
              {connection.name}
            </Text>
          </Group>
        ) : (
          <Text>Missing connection</Text>
        )
      }
    >
      {loading ? (
        <Stack mb="lg">
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Stack>
      ) : !connection && extractConnectionIds.length < 2 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          mt="sm"
          mb="xl"
          px="sm"
        >
          <Icon
            icon="AlertTriangleIcon"
            size={24}
            color={(theme) => theme.colors.orange[6]}
          />
          <Text ta="center" component="h3" fw={500} mx="auto" my={'xs'}>
            Failed to fetch connection
          </Text>
          <Text ta="center" component="p" mx="auto" mb="xl">
            An error has occurred. Please try fetching query again.
          </Text>
          <Group
            wrap="nowrap"
            sx={{
              width: '90%',
            }}
          >
            <Button
              onClick={closeModal}
              sx={{
                width: 150,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => refetch()}
              sx={{
                width: '100%',
              }}
            >
              Refetch
            </Button>
          </Group>
        </Box>
      ) : (
        <Box component="form" onSubmit={methods.handleSubmit(runExtraction)}>
          <Stack mb="lg">
            {/* Select for extractTypes */}
            {extractConnectionIds.length === 1 && (
              <Controller
                control={methods.control}
                name="extractType"
                rules={{
                  required: 'Data type is required',
                }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => {
                  return (
                    <TypeInput
                      extractors={extractors}
                      value={value}
                      error={error?.message}
                      setValue={onChange}
                    />
                  );
                }}
              />
            )}
            <Collapse in={showDateRangeInput}>
              <Stack gap="md">
                <Controller
                  control={methods.control}
                  name="dateRange"
                  render={({
                    field: { name, onChange, value },
                    fieldState: { error },
                  }) => {
                    return (
                      <InputWrapper label="Date range" error={error?.message}>
                        <InputDay
                          type="range"
                          value={value || undefined}
                          name={name}
                          onChange={onChange}
                          error={!!error?.message}
                          placeholder="Date range (optional)"
                          clearable
                          allowSingleDateInRange
                          popoverProps={{
                            withinPortal: true,
                            zIndex: 510,
                          }}
                        />
                      </InputWrapper>
                    );
                  }}
                />
                {dateRangeMethod && (
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                      backgroundColor: theme.colors.blue[0],
                      borderRadius: theme.radius.sm,
                      gap: theme.spacing.xs,
                      padding: theme.spacing.sm,
                    })}
                  >
                    <Icon icon="InfoIcon" size={16} color="#2563EB" />
                    <Text size="xs" c="gray.7">
                      The date range is targeting <b>{dateRangeMethod}</b>
                    </Text>
                  </Box>
                )}
              </Stack>
            </Collapse>
          </Stack>

          <Group justify="right" mb="md">
            <Button type="button" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={methods.formState.isSubmitting}
              type="submit"
            >
              Fetch data
            </Button>
          </Group>
        </Box>
      )}
    </Modal>
  );
};

const EXTRACT_ALL_OPTION: SelectItem<typeof EXTRACT_ALL_KEY> = {
  label: 'Fetch all types',
  value: EXTRACT_ALL_KEY,
};

const TypeInput = ({
  extractors,
  value,
  error,
  setValue,
}: {
  extractors: {
    name: string;
  }[];
  value: Maybe<string>;
  setValue: (value: string | null) => void;
  error: Maybe<string>;
}) => {
  'use memo';

  const data = useMemo<SelectItem[]>(
    () =>
      sortBy(extractors, 'name').map((ex) => ({
        label: toTitleCase(ex.name) || '',
        value: ex.name,
      })),
    [extractors]
  );

  const selectValue = useMemo<SelectItem | null>(
    () => [...data, EXTRACT_ALL_OPTION].find((d) => d.value === value) || null,
    [data, value]
  );

  const pinnedItems = useMemo<SelectItem[]>(() => [EXTRACT_ALL_OPTION], []);

  return (
    <InputWrapper label="Select data to fetch" error={error} required>
      <InputSelect
        data={{ options: data }}
        value={selectValue}
        type="single"
        setValue={(val) => setValue(val?.value || null)}
        inputProps={{
          placeholder: 'Data type',
          error: !!error,
          withClearButton: true,
        }}
        pinnedItems={pinnedItems}
        dropdownProps={{
          searchPlaceholder: 'Search data type...',
          width: 'target',
          withinPortal: true,
          zIndex: 510,
        }}
      />
    </InputWrapper>
  );
};
